import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useSnapshot } from 'valtio';

import store from '@/store';
import { Role } from '@/types/role';
import getRoles from '@/services/UserService/getRoles';
import Pagination, { PaginationRef } from '@/components/UiElements/Pagination';
import Splash from '@/components/splash/Splash';
import { ListInfoItem, ListRow } from '@/components/UiElements';

const perPage = 40;

type RoleScrollProps = {
    filter: string
    reloadRolesTrigger?: boolean
    onSelectedRoleChange: (role: Role) => void
}

const RoleScroll = ({
    filter,
    reloadRolesTrigger,
    onSelectedRoleChange,
}: RoleScrollProps) => {
    const [data, setData] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const snap = useSnapshot(store)
    const paginationRef = useRef<PaginationRef>(null);
    const isInitialMount = useRef(true);

    const loadRoles = useCallback(async () => {
        setLoading(true);

        try {
            const response: any = await getRoles();
            const roles = Array.isArray(response) ? response : response?.roles;
            setData(roles || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRoles();
    }, [loadRoles, reloadRolesTrigger, snap?.selectedCompanyId]);

    const filteredData = useMemo(() => {
        return filter ? data.filter(user => {
          return Object.values(user).some(value => 
            String(value).toLowerCase().includes(filter.toLowerCase())
          );
        }) : [...data];
    }, [data, filter]);

    const total = useMemo(() => Math.ceil(filteredData.length / perPage), [filteredData]);

    const handleGetPageData = useCallback((pageNumber: number) => {
        const start = (pageNumber - 1) * perPage;
        const end = start + perPage;
        return filteredData.slice(start, end);
    }, [filteredData]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        paginationRef.current?.reset();
    }, [filteredData]);
    
    return (
        <div className='relative min-h-[100%]'>
            {loading && <Splash />}
            {(filteredData.length > 0 && total > 0) ? (
                <Pagination
                    ref={paginationRef}
                    theme={(role: Role) => (
                        <ListRow
                            key={role.id}
                            title={<span className="capitalize">{role.name || 'Unnamed Role'}</span>}
                            minHeightClassName="min-h-[96px]"
                            onClick={() => onSelectedRoleChange(role)}
                        >
                            <ListInfoItem label="Description" value={role.description} className="min-w-[260px] max-w-[520px]" />
                        </ListRow>
                    )}
                    total={total}
                    offset={150}
                    getPageData={handleGetPageData}
                />
            ) : (
                !loading && (
                    <div className={'flex flex-row justify-center items-center px-mx py-xl text-gray-300 bg-gray-50 rounded-md mt-xxl gap-lg h-[150px]'}>
                        No roles match your current filters.
                    </div>
                )
            )}
        </div>
    );
}

export default RoleScroll;
